import { useBloc } from "@blac/react";
import { DateValue } from "@internationalized/date";
import { DataFieldProps } from "atom/datafield/dataFieldComponents";
import { AutocompleteComponentProps } from "molecule/autocomplete/Autocomplete";
import React, { ChangeEvent, useEffect, useMemo } from "react";
import { DateRange, Key } from "react-aria";
import { z } from "zod";
import { $ZodIssue } from "zod/v4/core";
import AutoFormBloc, {
  AutoFormRegisterProps,
  AnyFormFieldState,
  FormSchemaType,
  AutoFormBlocState,
  FormErrors
} from "./AutoFormBloc";
import AutoFormContext from "./AutoFormContext";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AutoFormChangeHandler = (value: any) => void;

function useAutoFormFieldProps({
  bloc,
  parseType,
  name,
  ref
}: {
  bloc: AutoFormBloc<FormSchemaType>;
  parseType: typeof String | typeof Number | typeof Array<string>;
  name: string;
  ref: React.Ref<AnyFormFieldState | null>;
}) {
  const fieldProps = useMemo(() => {
    if (bloc.props && parseType) {
      return bloc.registerAutoFormField(name, {
        type: parseType,
        ref: ref as { current: AnyFormFieldState | null }
      });
    }
  }, [name, parseType]);
  return fieldProps;
}

type ValidationResult = {
  issues: ($ZodIssue | undefined)[];
  shouldUpdate: boolean;
};

function validateFieldValue(
  value: unknown,
  fieldSchema: z.ZodType | undefined,
  currentError: $ZodIssue | undefined
): ValidationResult {
  const parseResult = fieldSchema?.safeParse(value);
  const issues = !parseResult?.success ? (parseResult?.error.issues ?? []) : [];
  const newIssue = issues[0];

  const shouldUpdate =
    currentError?.code !== newIssue?.code ||
    currentError?.message !== newIssue?.message;

  return { issues, shouldUpdate };
}

function createFieldChangeHandler<RT, T = undefined>(
  bloc: AutoFormBloc<FormSchemaType>,
  state: AutoFormBlocState<FormSchemaType>,
  name: string,
  transformValue?: (value: RT | T) => RT
) {
  return (value: RT | T) => {
    const transformedValue = transformValue ? transformValue(value) : value;
    bloc.handleChange(name, transformedValue);

    const fieldError = state.errors[name]?.[0];
    const hasValidationError = !!state.validationErrors?.[name];

    // Clear any manually set validation error when field changes
    if (hasValidationError) {
      bloc.setError(name, undefined);
    }

    if (!bloc.props?.validateOnChange && !fieldError) {
      return;
    }

    // Type assertion: schema.shape returns $ZodType (internal), but we need ZodType for validation
    const fieldSchema = state.schema?.shape[name] as z.ZodType | undefined;
    const { issues, shouldUpdate } = validateFieldValue(
      transformedValue,
      fieldSchema,
      fieldError
    );

    if (shouldUpdate) {
      bloc.patch({
        errors: {
          ...state.errors,
          [name]: issues
        }
      });
    }
  };
}

export type AutoFormFieldHookType =
  | "text"
  | "number"
  | "tel"
  | "date"
  | "daterange"
  | "autocomplete"
  | "checkboxGroup"
  | "range"
  | "select"
  | "radio"
  | "textarea"
  | "richtext";

export default function useAutoFormField<T = DataFieldProps>(
  userDefinedProps: T & {
    name: string;
    onSelectionChange?: (value: Key) => void;
  },
  type: AutoFormFieldHookType | string = "text",
  inputRef?: React.Ref<AnyFormFieldState | null>
): [AutoFormRegisterProps<T> | undefined] {
  const ref = React.useRef<AnyFormFieldState>(null);
  const { name } = userDefinedProps;
  React.useImperativeHandle(inputRef, () => {
    return ref.current as AnyFormFieldState;
  }, [ref]);

  const parseType = useMemo(() => {
    switch (type) {
      case "number":
      case "range":
      case "tel":
        return Number;
      case "checkboxGroup":
        return Array<string>;
      default:
        return String;
    }
  }, [type]);

  const contextId = React.useContext(AutoFormContext);
  const [state, bloc] = useBloc(AutoFormBloc, {
    id: contextId
  });

  const fieldPropsGenerated = useAutoFormFieldProps({
    bloc,
    ref,
    parseType,
    name
  });

  const handleInputChange = useMemo(
    () =>
      createFieldChangeHandler<
        AutoFormFieldHookType | string,
        ChangeEvent<HTMLInputElement>
      >(bloc, state, name, (e) => {
        if (e && typeof e === "object" && "target" in e) {
          return e.target.value;
        }
        return e;
      }),
    [bloc, state.errors, state.validationErrors, state.schema, name, parseType]
  );

  const handleSelectChange = useMemo(() => {
    const handler = createFieldChangeHandler<string, Key>(bloc, state, name);
    return (key: Key) => {
      userDefinedProps.onSelectionChange?.(key);
      handler(key);
    };
  }, [
    bloc,
    state.errors,
    state.validationErrors,
    state.schema,
    name,
    userDefinedProps.onSelectionChange
  ]);

  const changeHandlers: Record<
    AutoFormFieldHookType | string,
    AutoFormChangeHandler | undefined
  > = useMemo(
    () => ({
      date: createFieldChangeHandler<DateValue | undefined>(bloc, state, name),
      daterange: createFieldChangeHandler<DateRange>(bloc, state, name),
      select: handleSelectChange,
      autocomplete: handleSelectChange,
      radio: createFieldChangeHandler<string>(bloc, state, name),
      checkboxGroup: createFieldChangeHandler<string[]>(bloc, state, name),
      textarea: handleInputChange,
      richtext: createFieldChangeHandler<string | undefined>(bloc, state, name),
      number: handleInputChange,
      text: handleInputChange,
      tel: handleInputChange,
      range: handleInputChange
    }),
    [bloc, state, name, handleInputChange, handleSelectChange]
  );

  const additionalProps = useMemo(() => {
    const dynamicProps: Record<string, unknown> = {
      ref,
      onChangeCapture: changeHandlers[type] ?? handleInputChange
    };

    // Accepting `undefined` is what makes a field optional — an `instanceof
    // ZodOptional` check would miss wrappers like `.default()` or `.nullable()`.
    const fieldSchema = state.schema?.shape[name];
    dynamicProps["isRequired"] = !(
      fieldSchema && z.safeParse(fieldSchema, undefined).success
    );

    if (type === "checkboxGroup") {
      dynamicProps.onChange = dynamicProps.onChangeCapture;
    }

    if (type === "date") {
      dynamicProps.onDateChange = dynamicProps.onChangeCapture;
    }

    if (type === "textarea") {
      dynamicProps.onInput = dynamicProps.onChangeCapture;
    }

    if (type === "daterange") {
      dynamicProps.onChange = dynamicProps.onChangeCapture;
    }

    if (type === "autocomplete") {
      const { defaultValue, defaultSelectedKey } =
        userDefinedProps as unknown as AutocompleteComponentProps<{ id: "" }>;
      dynamicProps.onSelectionChange = dynamicProps.onChangeCapture;
      dynamicProps.defaultSelectedKey = defaultSelectedKey ?? defaultValue;
    }

    if (type === "select") {
      dynamicProps.onSelectionChange = dynamicProps.onChangeCapture;
    }

    const fieldError =
      state.errors[name]?.[0] ?? state.validationErrors?.[name];

    return {
      ...dynamicProps,
      error: fieldError,
      isInvalid: !!fieldError
    };
  }, [
    state.errors,
    state.validationErrors,
    state.schema,
    name,
    type,
    changeHandlers,
    userDefinedProps
  ]);

  useEffect(() => {
    return () => {
      bloc.unregisterAutoFormField(name);
    };
  }, [bloc, name]);

  return [
    { ...fieldPropsGenerated, ...additionalProps } as AutoFormRegisterProps<T>
  ];
}
